import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { getPatternTitle } from '../pattern-scanner/patternTutorialData';
import { resolveCanonicalBias, resolveCanonicalBreakout, resolveCoherenceSnapshot } from '../pattern-scanner/scannerExperienceShared';
import { fetchAuthenticatedJson } from '../../api/authenticatedFetch';

const defaultFormState = {
  symbol: 'SEI',
  exchange: 'BINANCE',
  timeframe: '15M',
  candles: '80',
  startTime: '2026-05-06T17:00',
  endTime: '2026-05-07T12:45',
  offset: '0',
  persist: false,
};

const presetCases = [
  {
    label: 'SEI Cup & Handle',
    symbol: 'SEI',
    exchange: 'BINANCE',
    timeframe: '15M',
    candles: '80',
    startTime: '2026-05-06T17:00',
    endTime: '2026-05-07T12:45',
  },
  {
    label: 'TON Double Top',
    symbol: 'TON',
    exchange: 'BINANCE',
    timeframe: '15M',
    candles: '80',
    startTime: '2026-05-06T17:00',
    endTime: '2026-05-07T12:45',
  },
  {
    label: 'JUP H&S',
    symbol: 'JUP',
    exchange: 'BINANCE',
    timeframe: '15M',
    candles: '80',
    startTime: '2026-05-09T13:00',
    endTime: '2026-05-10T08:45',
  },
];

function formatConfidence(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return '0';
  }
  const normalized = value <= 1 ? value * 100 : value;
  return String(Math.round(normalized));
}

function formatDateTime(value) {
  if (!value) {
    return 'n/a';
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  return parsed.toLocaleString('fr-FR');
}

function formatPriceLevel(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'n/a';
  }
  const digits = Math.abs(value) >= 100 ? 2 : Math.abs(value) >= 1 ? 4 : Math.abs(value) >= 0.1 ? 5 : 6;
  return value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: digits });
}

function formatRatio(value) {
  if (typeof value !== 'number' || Number.isNaN(value)) {
    return 'n/a';
  }
  return value.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

function directionColor(direction) {
  if (direction === 'BULLISH') return '#157f3b';
  if (direction === 'BEARISH') return '#b33a2f';
  return '#8f6b10';
}

function formatBiasValue(bias) {
  const code = String(bias?.code || '').toUpperCase();
  const bullishProbability = Number.isFinite(Number(bias?.bullishProbability)) ? Math.round(Number(bias.bullishProbability)) : null;
  const bearishProbability = Number.isFinite(Number(bias?.bearishProbability)) ? Math.round(Number(bias.bearishProbability)) : null;

  if (code === 'BULLISH') {
    return bullishProbability !== null ? `Haussier ${bullishProbability}%` : 'Haussier';
  }
  if (code === 'BEARISH') {
    return bearishProbability !== null ? `Baissier ${bearishProbability}%` : 'Baissier';
  }
  if (bullishProbability !== null && bearishProbability !== null) {
    return `Neutre ${bullishProbability}% / ${bearishProbability}%`;
  }
  return 'Neutre';
}

function formatBreakoutStatusQualifier(status) {
  switch (String(status || '').toUpperCase()) {
    case 'PRE_BREAKOUT':
      return 'pré-cassure';
    case 'CANDIDATE_BREAKOUT':
      return 'en approche';
    case 'CONFIRMED_BREAKOUT':
      return 'confirmée';
    case 'RETEST_PHASE':
      return 'en retest';
    default:
      return '';
  }
}

function formatBreakoutValue(breakout) {
  if (!breakout) {
    return 'n/a';
  }
  const typeLabel = String(breakout.type || '').toUpperCase() === 'BREAKDOWN'
    ? 'Cassure baissière'
    : String(breakout.type || '').toUpperCase() === 'BREAKOUT'
      ? 'Cassure haussière'
      : 'Cassure';
  const qualifier = formatBreakoutStatusQualifier(breakout.status);
  return qualifier ? `${typeLabel} · ${qualifier}` : typeLabel;
}

function formatCoherenceValue(coherence) {
  switch (String(coherence?.overallStatus || '').toUpperCase()) {
    case 'CONSISTENT':
      return 'Cohérente';
    case 'MIXED':
      return 'Mixte';
    case 'INCONSISTENT':
      return 'Incohérente';
    default:
      return 'n/a';
  }
}

function buildReplayImageUrl(imagePath) {
  if (!imagePath) {
    return '';
  }
  const query = new URLSearchParams({ imagePath });
  return `/api/admin/pattern/replay/image?${query.toString()}`;
}

function StatBadge({ label, value, accent = '#19323c' }) {
  return (
    <Box
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 3,
        background: '#fff',
        border: '1px solid rgba(18,44,52,0.08)',
        minWidth: 120,
      }}
    >
      <Typography variant="caption" sx={{ color: '#6c7a80', display: 'block', mb: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: accent, fontWeight: 700 }}>
        {value}
      </Typography>
    </Box>
  );
}

const PatternReplayAdminPage = () => {
  const [mode, setMode] = useState('range');
  const [formState, setFormState] = useState(defaultFormState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);
  const [imageLoadFailed, setImageLoadFailed] = useState(false);

  const currentAnalysis = result?.analysis ?? null;
  const imageUrl = useMemo(() => buildReplayImageUrl(currentAnalysis?.imagePath), [currentAnalysis?.imagePath]);
  const canonicalBias = useMemo(() => resolveCanonicalBias(currentAnalysis || {}), [currentAnalysis]);
  const canonicalBreakout = useMemo(() => resolveCanonicalBreakout(currentAnalysis || {}), [currentAnalysis]);
  const coherenceSnapshot = useMemo(() => resolveCoherenceSnapshot(currentAnalysis || {}), [currentAnalysis]);

  const updateField = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormState((current) => ({ ...current, [field]: value }));
  };

  const applyPreset = (preset) => {
    setMode('range');
    setImageLoadFailed(false);
    setResult(null);
    setError('');
    setFormState((current) => ({
      ...current,
      symbol: preset.symbol,
      exchange: preset.exchange,
      timeframe: preset.timeframe,
      candles: preset.candles,
      startTime: preset.startTime,
      endTime: preset.endTime,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError('');
    setResult(null);
    setImageLoadFailed(false);

    try {
      if (mode === 'range' && (!formState.startTime || !formState.endTime)) {
        throw new Error('Le mode range attend un startTime et un endTime.');
      }
      if (mode === 'range' && formState.endTime <= formState.startTime) {
        throw new Error('endTime doit etre strictement apres startTime.');
      }

      const query = new URLSearchParams({
        symbol: formState.symbol.trim().toUpperCase(),
        exchange: formState.exchange.trim().toUpperCase(),
        timeframe: formState.timeframe.trim().toUpperCase(),
        candles: formState.candles || '80',
        persist: String(formState.persist),
      });
      if (mode === 'offset') {
        query.set('offset', formState.offset || '0');
      } else {
        query.set('startTime', formState.startTime);
        query.set('endTime', formState.endTime);
      }

      const payload = await fetchAuthenticatedJson(`/api/admin/pattern/replay?${query.toString()}`);
      setResult(payload);
    } catch (submissionError) {
      setError(submissionError instanceof Error ? submissionError.message : 'Erreur inconnue');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 4, background: 'linear-gradient(180deg, #f4efe6 0%, #eef3f6 100%)', minHeight: '100%' }}>
      <Stack spacing={3}>
        <Paper sx={{ p: 3.5, borderRadius: 5, background: 'linear-gradient(135deg, #17323c 0%, #355b63 100%)', color: '#f7f3ea' }}>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} justifyContent="space-between">
            <Box sx={{ maxWidth: 760 }}>
              <Chip label="Admin Replay" sx={{ bgcolor: 'rgba(247,243,234,0.14)', color: '#f7f3ea', fontWeight: 700, mb: 1.5 }} />
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 1.25 }}>
                Rejouer une fenetre historique exacte
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(247,243,234,0.82)', lineHeight: 1.7 }}>
                Ce panneau rejoue une fenetre precise ou un offset historique sur le moteur pattern, sans dependre du scanner live du moment. Utilisez le mode range pour un intervalle exact ou le mode offset pour rejouer une fenetre N bougies avant la bougie la plus recente.
              </Typography>
            </Box>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ alignSelf: 'flex-start' }}>
              {presetCases.map((preset) => (
                <Button
                  key={preset.label}
                  variant="outlined"
                  onClick={() => applyPreset(preset)}
                  sx={{
                    color: '#f7f3ea',
                    borderColor: 'rgba(247,243,234,0.4)',
                    borderRadius: 99,
                    '&:hover': { borderColor: '#f7f3ea', background: 'rgba(247,243,234,0.08)' },
                  }}
                >
                  {preset.label}
                </Button>
              ))}
            </Stack>
          </Stack>
        </Paper>

        <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} alignItems="stretch">
          <Paper sx={{ flex: 0.95, p: 3, borderRadius: 5, background: '#fffaf2' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
              Parametres de replay
            </Typography>
            <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
              Les horodatages sont interpretes en UTC. Le mode offset rejoue la fenetre qui se termine N bougies avant la plus recente.
            </Typography>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2.5}>
                <ToggleButtonGroup
                  value={mode}
                  exclusive
                  onChange={(_, nextValue) => {
                    if (nextValue) {
                      setMode(nextValue);
                    }
                  }}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  <ToggleButton value="range">Range</ToggleButton>
                  <ToggleButton value="offset">Offset</ToggleButton>
                </ToggleButtonGroup>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <TextField label="Symbol" value={formState.symbol} onChange={updateField('symbol')} fullWidth required />
                  <TextField label="Exchange" value={formState.exchange} onChange={updateField('exchange')} fullWidth required />
                  <TextField label="Timeframe" value={formState.timeframe} onChange={updateField('timeframe')} fullWidth required />
                  <TextField label="Candles" type="number" value={formState.candles} onChange={updateField('candles')} fullWidth required inputProps={{ min: 50, max: 500 }} />
                </Stack>

                {mode === 'range' ? (
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                    <TextField
                      label="Start Time UTC"
                      type="datetime-local"
                      value={formState.startTime}
                      onChange={updateField('startTime')}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="End Time UTC"
                      type="datetime-local"
                      value={formState.endTime}
                      onChange={updateField('endTime')}
                      fullWidth
                      required
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>
                ) : (
                  <TextField
                    label="Offset en bougies"
                    type="number"
                    value={formState.offset}
                    onChange={updateField('offset')}
                    required
                    inputProps={{ min: 0 }}
                    sx={{ maxWidth: 280 }}
                  />
                )}

                <FormControlLabel
                  control={<Checkbox checked={formState.persist} onChange={updateField('persist')} />}
                  label="Persister cette analyse dans l'historique pattern"
                />

                {error && <Alert severity="error">{error}</Alert>}

                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
                  <Button type="submit" variant="contained" disabled={loading} sx={{ borderRadius: 99, px: 3, py: 1.25 }}>
                    {loading ? 'Replay en cours...' : 'Lancer le replay'}
                  </Button>
                  <Typography variant="body2" sx={{ color: '#667983' }}>
                    Le replay ne depend pas du top100 courant: il rejoue exactement la fenetre demandee.
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </Paper>

          <Paper sx={{ flex: 1.05, p: 3, borderRadius: 5, background: '#fff' }}>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#19323c', mb: 0.75 }}>
              Resultat du replay
            </Typography>
            <Typography variant="body2" sx={{ color: '#4f646f', mb: 3 }}>
              Le moteur retourne le pattern detecte, la fenetre effectivement analysee et, si disponible, le visuel annote genere par le moteur Python.
            </Typography>

            {!currentAnalysis ? (
              <Alert severity="info">Aucun replay lance pour le moment.</Alert>
            ) : (
              <Stack spacing={3}>
                <Paper sx={{ p: 3, borderRadius: 4, border: '1px solid rgba(18,44,52,0.08)', background: 'linear-gradient(180deg, #ffffff 0%, #f6f0e5 100%)' }}>
                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={2.5} justifyContent="space-between" sx={{ mb: 2 }}>
                    <Box>
                      <Typography variant="overline" sx={{ color: '#6c7a80', letterSpacing: '0.12em' }}>
                        Pattern detecte
                      </Typography>
                      <Typography variant="h4" sx={{ fontWeight: 800, color: '#19323c' }}>
                        {getPatternTitle(currentAnalysis.patternDetected)}
                      </Typography>
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} useFlexGap flexWrap="wrap" sx={{ alignSelf: 'flex-start' }}>
                      <Chip
                        label={`Biais ${formatBiasValue(canonicalBias)}`}
                        sx={{ bgcolor: directionColor(canonicalBias.code), color: '#fff', fontWeight: 700 }}
                      />
                      <Chip
                        label={formatBreakoutValue(canonicalBreakout)}
                        sx={{ bgcolor: '#1d3557', color: '#fff', fontWeight: 700 }}
                      />
                      {coherenceSnapshot ? (
                        <Chip label={`Cohérence ${formatCoherenceValue(coherenceSnapshot)}`} sx={{ bgcolor: '#264653', color: '#fff', fontWeight: 700 }} />
                      ) : null}
                    </Stack>
                  </Stack>

                  <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                    <StatBadge label="Biais" value={formatBiasValue(canonicalBias)} />
                    <StatBadge label="Cassure" value={formatBreakoutValue(canonicalBreakout)} accent="#1d3557" />
                    <StatBadge label="Confiance" value={`${formatConfidence(currentAnalysis.confidence)}%`} />
                    <StatBadge label="Prob. pump" value={`${currentAnalysis.pumpProbability}%`} accent="#157f3b" />
                    <StatBadge label="Prob. dump" value={`${currentAnalysis.dumpProbability}%`} accent="#b33a2f" />
                    <StatBadge label="Fakeout" value={`${currentAnalysis.fakeoutRisk}%`} accent="#8f6b10" />
                    <StatBadge label="Breakout" value={formatPriceLevel(currentAnalysis.breakoutLevel)} accent="#1d3557" />
                    <StatBadge label="Target" value={formatPriceLevel(currentAnalysis.targetPrice)} accent="#157f3b" />
                    <StatBadge label="Stop" value={formatPriceLevel(currentAnalysis.stopInvalidation)} accent="#b33a2f" />
                    <StatBadge label="R/R" value={formatRatio(currentAnalysis.riskRewardRatio)} accent="#264653" />
                    <StatBadge label="Confiance cible" value={typeof currentAnalysis.targetConfidence === 'number' ? `${formatConfidence(currentAnalysis.targetConfidence)}%` : 'n/a'} accent="#6d597a" />
                    <StatBadge label="Score pattern" value={`${currentAnalysis.patternScore}`} />
                    <StatBadge label="Source" value={currentAnalysis.analysisSource} />
                  </Stack>

                  <Typography variant="body1" sx={{ color: '#2e434c', lineHeight: 1.7 }}>
                    {currentAnalysis.aiSummary}
                  </Typography>
                </Paper>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
                  <StatBadge label="Mode replay" value={result.replayMode} />
                  <StatBadge label="Source bougies" value={result.candleSource || 'n/a'} />
                  <StatBadge label="Bougies retournees" value={`${result.returnedCandles}/${result.requestedCandles}`} />
                  <StatBadge label="Persisted" value={result.persisted ? 'Oui' : 'Non'} />
                  {result.offset !== null && result.offset !== undefined ? <StatBadge label="Offset" value={`${result.offset}`} /> : null}
                </Stack>

                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap>
                  <StatBadge label="Fenetre debut" value={formatDateTime(result.windowStart)} />
                  <StatBadge label="Fenetre fin" value={formatDateTime(result.windowEnd)} />
                  <StatBadge label="Start demande" value={formatDateTime(result.requestedStartTime)} />
                  <StatBadge label="End demande" value={formatDateTime(result.requestedEndTime)} />
                </Stack>

                <Box sx={{ p: 2.5, borderRadius: 4, background: '#f9f4ea', border: '1px solid rgba(18,44,52,0.08)' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#19323c', mb: 0.5 }}>
                    Preuve visuelle
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#4f646f', mb: 2 }}>
                    Le PNG ci-dessous provient directement du replay historique qui vient d'etre rejoue.
                  </Typography>

                  {currentAnalysis.imagePath && !imageLoadFailed ? (
                    <>
                      <Box
                        component="img"
                        src={imageUrl}
                        alt={`Replay ${currentAnalysis.patternDetected} ${currentAnalysis.symbol}`}
                        onError={() => setImageLoadFailed(true)}
                        sx={{ width: '100%', borderRadius: 3, border: '1px solid rgba(18,44,52,0.08)', background: '#f3ede1', objectFit: 'cover' }}
                      />
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                        <StatBadge label="Version image" value={currentAnalysis.imageVersion || 'v1'} />
                        <StatBadge label="Image generee le" value={formatDateTime(currentAnalysis.imageGeneratedAt)} />
                        <StatBadge label="Analyse le" value={formatDateTime(currentAnalysis.analysisTimestamp)} />
                      </Stack>
                    </>
                  ) : (
                    <Alert severity="info">
                      Aucun PNG annote n'a ete recupere pour ce replay. Le moteur local a peut-etre gagne l'arbitrage sans image Python exploitable.
                    </Alert>
                  )}
                </Box>
              </Stack>
            )}
          </Paper>
        </Stack>
      </Stack>
    </Box>
  );
};

export default PatternReplayAdminPage;